import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUser } from '../../../context/UserContext';
import { useEffect, useMemo, useState } from 'react';
import { CountItem as CountItemType } from '../../mypage/payment/RemainingTicket';
import QuotaService from '../../../service/quota/Quota';
import { isLogin } from '../../../utils/auth';

type MembershipLabel = 'Free' | 'Promotion' | 'Standard' | 'Premium';

function RemainingTickets() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [userCount, setUserCount] = useState<CountItemType[]>([]);

  const membershipLabelMap: Record<string, MembershipLabel> = {
    P: 'Premium',
    S: 'Standard',
    T: 'Promotion',
  };

  const label: MembershipLabel = user?.plan_grade
    ? (membershipLabelMap[user.plan_grade] ?? 'Free')
    : 'Free';

  const getCount = async () => {
    try {
      const res = await QuotaService.count();
      setUserCount(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!isLogin()) return;
    getCount();
  }, []);

  const getValue = (code: string) => {
    const f = userCount.find((x) => x.type === code);
    return { count: f?.count ?? 0, limit: f?.limit ?? 0 };
  };

  const membershipTicketCount = useMemo(
    () => [
      {
        label: '경공매 정보 열람',
        ...getValue('A'),
      },
      {
        label: 'AI 탐색권',
        ...getValue('chat'),
      },
      {
        label: '퀀트 리포트 열람권',
        ...getValue('B'),
      },
      {
        label: '알고리즘 만들기',
        ...getValue('D'),
      },
    ],
    [userCount],
  );
  const fmt = (n: number) => n.toLocaleString('ko-KR');

  return (
    <Container>
      <TitleSection>
        <Title>잔여 이용권</Title>
        <MoreButton
          onClick={() => {
            navigate('/mypage');
            sessionStorage.setItem('mypageTab', 'payment');
          }}
        >
          전체보기 {'>'}
        </MoreButton>
      </TitleSection>
      {label === 'Free' ? (
        <FreeCountTicketSection>
          <FreeCountTicketTitle>
            보유 중인 이용권이 없습니다.
          </FreeCountTicketTitle>
          <FreeCountTicketButton onClick={() => navigate('/payment/plan')}>
            이용권 구매
          </FreeCountTicketButton>
        </FreeCountTicketSection>
      ) : (
        <Content>
          {membershipTicketCount.map((item) => (
            <CountItem key={item.label}>
              <CountItemLabel>{item.label}</CountItemLabel>
              <CountItemData>
                {label === 'Premium' ? (
                  <CountItemData>
                    <span>무제한</span>
                  </CountItemData>
                ) : (
                  <CountItemData>
                    <span>{fmt(item.limit - item.count)}</span>/
                    {fmt(item.limit)}건
                  </CountItemData>
                )}
              </CountItemData>
            </CountItem>
          ))}
        </Content>
      )}
    </Container>
  );
}

export default RemainingTickets;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const TitleSection = styled.div`
  display: flex;
  padding-top: 8px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const Title = styled.div`
  flex: 1 0 0;
  color: #8b95a1;
  font-size: 13px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.26px;
`;

const MoreButton = styled.div`
  display: flex;
  align-items: center;

  color: #616161;
  font-size: 13px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.26px;
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 8px;
  align-self: stretch;
`;

const CountItem = styled.div`
  display: flex;
  padding: 10px 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1 0 0;
`;

const CountItemLabel = styled.div`
  color: rgba(0, 12, 30, 0.8);
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.26px;
`;

const CountItemData = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;

  color: #8b95a1;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.26px;

  & > span {
    color: #222;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%;
    letter-spacing: -0.3px;
  }
`;

const FreeCountTicketSection = styled.div`
  display: flex;
  padding: 16px 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const FreeCountTicketTitle = styled.div`
  align-self: stretch;
  color: rgba(0, 12, 30, 0.8);
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const FreeCountTicketButton = styled.button`
  border: none;
  outline: none;
  cursor: pointer;

  display: flex;
  padding: 12px 0;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  background: #008aff;

  color: #fff;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.32px;
`;
